import React, { useState } from 'react';
import { MessageSquare, Coffee, X, Send, CheckCircle, Heart } from 'lucide-react';

interface FeedbackPromptProps {
  isVisible: boolean;
  onClose: () => void;
  operationType: 'success' | 'error';
  toolName: string;
}

const TELEGRAM_BOT_TOKEN = '8026566523:AAGUsfaNBr2ALyfBzgmWxZjQCBVqE_hzs0o';
// Chat ID for feedback messages
const TELEGRAM_CHAT_ID = process.env.REACT_APP_TELEGRAM_CHAT_ID || '7618951314';

const FeedbackPrompt: React.FC<FeedbackPromptProps> = ({ 
  isVisible, 
  onClose, 
  operationType,
  toolName 
}) => {
  const [feedback, setFeedback] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);

  const sendFeedbackToTelegram = async () => {
    if (!feedback.trim()) return;
    
    setSending(true);
    try {
      const message = `📝 *AllTools Feedback*\n\n🔧 Tool: ${toolName}\n📊 Status: ${operationType === 'success' ? '✅ Success' : '❌ Error'}\n\n💬 Message:\n${feedback}\n\n_Sent from AllTools website_`;
      
      const response = await fetch(`https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}/sendMessage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chat_id: TELEGRAM_CHAT_ID,
          text: message,
          parse_mode: 'Markdown',
        }),
      });

      if (response.ok) {
        setSent(true);
        setFeedback('');
        setTimeout(() => {
          setSent(false);
          setShowFeedbackForm(false);
          onClose();
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to send feedback:', error);
    } finally {
      setSending(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-fade-in">
        {/* Header */}
        <div className={`p-6 ${operationType === 'success' ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gradient-to-r from-orange-500 to-red-500'} text-white relative`}>
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-3">
            {operationType === 'success' ? (
              <CheckCircle className="w-10 h-10" />
            ) : (
              <MessageSquare className="w-10 h-10" />
            )}
            <div>
              <h3 className="text-xl font-bold">
                {operationType === 'success' ? 'Operation Complete!' : 'Something went wrong'}
              </h3>
              <p className="text-white/80 text-sm">
                {operationType === 'success' 
                  ? 'Your file has been processed successfully' 
                  : 'We encountered an issue processing your file'}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {!showFeedbackForm ? (
            <>
              <p className="text-gray-600 text-center">
                {operationType === 'success' 
                  ? 'Enjoying AllTools? Your support helps us keep it free!' 
                  : 'Help us improve by sharing what went wrong'}
              </p>

              <div className="flex flex-col gap-3">
                {/* Feedback Button */}
                <button
                  onClick={() => setShowFeedbackForm(true)}
                  className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors font-medium"
                >
                  <MessageSquare className="w-5 h-5" />
                  {operationType === 'success' ? 'Share Feedback' : 'Report Issue'}
                </button>

                {/* Donation Button */}
                <a
                  href="https://buymeacoffee.com/dandev2026"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-500 hover:to-orange-500 text-gray-900 rounded-xl transition-all font-medium shadow-md hover:shadow-lg"
                >
                  <Coffee className="w-5 h-5" />
                  Buy me a coffee ☕
                </a>

                {/* Close Button */}
                <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-700 text-sm transition-colors"
                >
                  Maybe later
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Feedback Form */}
              {sent ? (
                <div className="text-center py-4">
                  <Heart className="w-12 h-12 text-red-500 mx-auto mb-3 animate-pulse" />
                  <p className="text-lg font-medium text-gray-800">Thank you!</p>
                  <p className="text-gray-600">Your feedback means a lot to us</p>
                </div>
              ) : (
                <>
                  <textarea
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    placeholder={operationType === 'success' 
                      ? "What do you love about AllTools? Any suggestions?" 
                      : "Please describe what happened..."}
                    className="w-full p-3 border border-gray-300 rounded-xl resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={4}
                  />
                  
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowFeedbackForm(false)}
                      className="flex-1 py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors"
                    >
                      Back
                    </button>
                    <button
                      onClick={sendFeedbackToTelegram}
                      disabled={sending || !feedback.trim()}
                      className="flex-1 flex items-center justify-center gap-2 py-2 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-xl transition-colors"
                    >
                      {sending ? (
                        'Sending...'
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Send
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackPrompt;
